import { noNullQuerySelector } from "../src/utils/dom";

// Check if email or cell number already exists in the system
async function checkUserExists(
  field: "email" | "cell_no",
  value: string,
): Promise<boolean> {
  if (!value.trim()) return false;

  try {
    const response = await fetch(
      `/users/is-user-exists?user[${field}]=${encodeURIComponent(value)}`,
    );
    const data = await response.json();
    return data.is_user_exists;
  } catch {
    return false;
  }
}

// Show error on input field
function showFieldError(input: HTMLInputElement, errorMessage: string): void {
  input.classList.remove("is-valid");
  input.classList.add("is-invalid");
  input.setCustomValidity(errorMessage);

  const errorEl = input.parentElement?.querySelector(".invalid-feedback");
  if (errorEl) {
    errorEl.textContent = errorMessage;
  }
}

// Clear error on input field
function clearFieldError(input: HTMLInputElement): void {
  input.setCustomValidity("");

  if (input.validity.valid) {
    input.classList.remove("is-invalid");
  }

  // Reset error message to default
  const errorEl = input.parentElement?.querySelector(".invalid-feedback");
  if (errorEl) {
    if (
      input.type === "email" ||
      input.classList.contains("child-email-field")
    ) {
      errorEl.textContent = "Please input a valid email address";
    } else if (input.classList.contains("child-cell-field")) {
      errorEl.textContent = "Please input a valid phone number";
    }
  }
}

// Attach validation to email fields
function attachEmailValidation(input: HTMLInputElement): void {
  input.addEventListener("blur", async () => {
    const email = input.value.trim();
    if (!email) return;

    const exists = await checkUserExists("email", email);
    if (exists) {
      showFieldError(
        input,
        "This email is already linked with an existing account",
      );
      return;
    } else {
      clearFieldError(input);
    }

    const existingEmails = [
      ...document.querySelectorAll<HTMLInputElement>(".child-email-field"),
    ]
      .filter((child) => child.id !== input.id)
      .map((child) => child.value);

    if (input.id !== "parent-email") {
      const parentEmail =
        document.querySelector<HTMLInputElement>("#parent-email");

      if (parentEmail) existingEmails.push(parentEmail.value);
    }

    if (existingEmails.includes(email)) {
      showFieldError(input, "You cannot re-enter the same email");
    } else {
      clearFieldError(input);
    }
  });
}

// Attach validation to cell number fields
function attachCellValidation(input: HTMLInputElement): void {
  input.addEventListener("blur", async () => {
    const cellNo = input.value.trim();
    if (!cellNo) return;

    const exists = await checkUserExists("cell_no", cellNo);
    if (exists) {
      showFieldError(
        input,
        "This phone number is already linked with an existing account",
      );
      return;
    } else {
      clearFieldError(input);
    }

    const existingCellNos = [
      ...document.querySelectorAll<HTMLInputElement>(".child-cell-field"),
    ]
      .filter((child) => child.id !== input.id)
      .map((child) => child.value);

    if (input.id !== "parent-cell-no") {
      const parentCellNo =
        document.querySelector<HTMLInputElement>("#parent-cell-no");

      if (parentCellNo) existingCellNos.push(parentCellNo.value);
    }

    if (existingCellNos.includes(cellNo)) {
      showFieldError(input, "You cannot re-enter the same phone number");
    } else {
      clearFieldError(input);
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  const addAnotherChildButton = noNullQuerySelector<HTMLButtonElement>(
    "#add-another-child-btn",
  );
  const childDetailsContainer =
    noNullQuerySelector<HTMLDivElement>(".child-details");

  // Attach validation to parent email and cell fields
  const parentEmailField =
    document.querySelector<HTMLInputElement>("#parent-email");
  const parentCellField =
    document.querySelector<HTMLInputElement>("#parent-cell-no");

  if (parentEmailField) attachEmailValidation(parentEmailField);
  if (parentCellField) attachCellValidation(parentCellField);

  // Attach validation to initial child fields
  document
    .querySelectorAll<HTMLInputElement>(".child-email-field")
    .forEach(attachEmailValidation);
  document
    .querySelectorAll<HTMLInputElement>(".child-cell-field")
    .forEach(attachCellValidation);

  addAnotherChildButton.addEventListener("click", () => {
    const randomId = Math.floor(Math.random() * 10000);
    const childDetailsClone = childDetailsContainer.cloneNode(
      true,
    ) as HTMLElement;
    const removeChildDetailsButton = noNullQuerySelector<HTMLButtonElement>(
      ".remove-child-details",
      childDetailsClone,
    );

    // Clear all input values in the cloned section
    childDetailsClone
      .querySelectorAll<HTMLInputElement>("input")
      .forEach((input) => {
        input.value = "";
        input.classList.remove("is-valid", "is-invalid");
        input.setCustomValidity("");
      });

    // Reset select elements to default (first option - prompt)
    childDetailsClone
      .querySelectorAll<HTMLSelectElement>("select")
      .forEach((select) => {
        select.selectedIndex = 0;
        select.classList.remove("is-valid", "is-invalid");
      });

    // Clear any error messages and reset to defaults
    childDetailsClone
      .querySelectorAll<HTMLElement>(".invalid-feedback")
      .forEach((el) => {
        el.classList.remove("d-block");
      });
    const childEmailError =
      childDetailsClone.querySelector(".child-email-error");
    if (childEmailError) {
      childEmailError.textContent = "Please input a valid email address";
    }
    const childCellError = childDetailsClone.querySelector(".child-cell-error");
    if (childCellError) {
      childCellError.textContent = "Please input a valid phone number";
    }

    // Attach validation to new child fields
    const childEmailField =
      childDetailsClone.querySelector<HTMLInputElement>(".child-email-field");
    const childCellField =
      childDetailsClone.querySelector<HTMLInputElement>(".child-cell-field");

    if (childEmailField) {
      childEmailField.id = `child-email-${randomId}`;
      attachEmailValidation(childEmailField);
    }
    if (childCellField) {
      childCellField.id = `child-cell-${randomId}`;
      attachCellValidation(childCellField);
    }

    removeChildDetailsButton.classList.remove("d-none");

    // Find the last child-details element and insert after it
    const allChildDetails = document.querySelectorAll(".child-details");
    const lastChildDetails = allChildDetails[allChildDetails.length - 1];
    if (lastChildDetails) {
      lastChildDetails.parentElement?.insertBefore(
        childDetailsClone,
        lastChildDetails.nextSibling,
      );
    }

    removeChildDetailsButton.addEventListener("click", () => {
      childDetailsClone.remove();
    });
  });
});
